import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { createPrescription, reroutePrescription } from '../../api/encounters.js'
import { searchDrugs } from '../../api/catalogues.js'
import OrgSelect from '../../components/OrgSelect.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueAutocomplete from './CatalogueAutocomplete.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import { statusColor, formatDateTime } from '../../utils/format.js'
import { ROUTES, COMMON_DRUGS } from '../../constants/index.js'
import { calculateQuantity, formatQuantityHint, totalForDispensing } from '../../utils/prescriptionMath.js'
import OrderResultsPanel from './OrderResultsPanel.jsx'
import usePersistedState from '../../hooks/usePersistedState.js'

function newItem() {
  return {
    drug_code: '', drug_name: '',
    strength: '',            // copied from the pharmacy catalogue on pick
    route: 'PO',             // sensible default — most outpatient orders
    dose: '', frequency: '', duration: '',
    quantity: '',            // blank by default; calculator suggests one
    instructions: '',
  }
}

export default function PrescriptionForm({ encounter, locked }) {
  const qc = useQueryClient()
  // Persist transient draft to localStorage so half-built prescriptions
  // survive HMR / tab close / re-mounts. Cleared on successful submit.
  const draftKey = `rx:enc${encounter.id}`
  const [pharmacyId, setPharmacyId] = usePersistedState(`${draftKey}:pharmacy`, null)
  const [notes, setNotes] = usePersistedState(`${draftKey}:notes`, '')
  const [items, setItems] = usePersistedState(`${draftKey}:items`, [newItem()])
  const [formError, setFormError] = useState(null)

  const mut = useMutation({
    mutationFn: (body) => createPrescription(encounter.id, body),
    onSuccess: (newRx) => {
      // Splice the new prescription into the encounter cache directly
      // from the POST response so the row appears instantly without
      // waiting for an invalidate + refetch round-trip.
      qc.setQueryData(['encounter', encounter.id], (prev) => {
        if (!prev || !newRx) return prev
        return {
          ...prev,
          prescriptions: [...(prev.prescriptions || []), newRx],
        }
      })
      qc.invalidateQueries({ queryKey: ['encounter', encounter.id] })
      setItems([newItem()]); setNotes('')
    },
  })

  function updateItem(i, patch) {
    setItems((curr) => curr.map((x, idx) => idx === i ? { ...x, ...patch } : x))
  }
  function removeItem(i) {
    setItems((curr) => curr.filter((_, idx) => idx !== i))
  }

  function onSubmit(e) {
    e.preventDefault()
    setFormError(null)
    if (!pharmacyId) return setFormError('Select a target pharmacy.')
    // Drug name is required; drug_code is optional — items can be free-text
    // when the pharmacy hasn't stocked the drug yet.
    const filled = items.filter((it) => (it.drug_name || '').trim().length > 0)
    if (filled.length === 0) {
      return setFormError(
        "Add at least one drug. Type a name and pick from the dropdown — or use the '+ Use as typed' option if it isn't in this pharmacy's catalogue."
      )
    }
    mut.mutate({
      target_pharmacy_id: pharmacyId,
      notes,
      items: filled,
    })
  }

  const existing = encounter.prescriptions || []

  return (
    <Card>
      <CardHeader title="Prescriptions" subtitle="Route to a pharmacy on submit" />

      {existing.length > 0 && (
        <div className="space-y-3 mb-4">
          {existing.map((rx) => (
            <div key={rx.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${statusColor(rx.status)}`}>{rx.status}</span>
                <span className="font-mono text-slate-500">#{rx.id}</span>
                {rx.pharmacy_name && (
                  <span className="text-slate-700"><span className="text-slate-400">→</span> {rx.pharmacy_name}</span>
                )}
                {rx.created_at && <span className="text-slate-400">· Ordered {formatDateTime(rx.created_at)}</span>}
              </div>

              {(rx.items || []).length > 0 ? (
                <ul className="mt-2 divide-y divide-slate-100">
                  {rx.items.map((it) => (
                    <RxItemRow
                      key={it.id || it.drug_code}
                      item={it}
                      prescriptionId={rx.id}
                      onRerouted={() => qc.invalidateQueries({ queryKey: ['encounter', encounter?.id] })}
                    />
                  ))}
                </ul>
              ) : rx.medication_name ? (
                <div className="mt-2 text-sm">{rx.medication_name} {rx.dosage} {rx.frequency} {rx.duration}</div>
              ) : null}

              {rx.notes && (
                <div className="mt-2 text-[11px] text-slate-500">
                  <span className="uppercase tracking-wide text-slate-400">Clinician note </span>
                  {rx.notes}
                </div>
              )}

              {/*
                Pharmacy fulfilment — confirms the patient picked up
                the script. Pharmacies can also attach dispensing
                receipts / signed prescription scans for the record.
                Doctor sees a green "Fulfilled" badge when status is
                'dispensed' so they know the patient has the meds.
              */}
              <OrderResultsPanel
                kind="rx"
                encounter={encounter}
                orderId={rx.id}
                status={rx.status}
                summary={rx.dispensing_notes}
                summaryLabel="Dispensing notes"
              />
            </div>
          ))}
        </div>
      )}

      {locked ? (
        <div className="text-xs text-slate-400">
          {existing.length === 0
            ? 'No prescriptions were recorded on this encounter.'
            : 'Encounter is locked. Add new orders via an amended encounter.'}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Target pharmacy</span>
            <div className="mt-1">
              <OrgSelect orgType="pharmacy" value={pharmacyId} onChange={setPharmacyId} placeholder="Select pharmacy…" />
            </div>
          </label>

          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} className="border border-slate-200 rounded p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Item {i + 1}</div>
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="text-xs text-rose-600">Remove</button>
                  )}
                </div>

                {(it.drug_code || it.drug_name) ? (
                  <>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      {it.drug_code
                        ? <span className="font-mono text-xs text-wantok-700 mr-2">{it.drug_code}</span>
                        : <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 mr-2">free text</span>}
                      <span className="font-medium">{it.drug_name}</span>
                      {it.strength && <span className="text-slate-500 ml-2 text-xs">{it.strength}</span>}
                    </div>
                    <button type="button" onClick={() => updateItem(i, { drug_code: '', drug_name: '', strength: '' })} className="text-xs text-slate-500 underline">Change</button>
                  </div>
                  {/* Inline availability + price hint at the chosen pharmacy.
                      Only renders when the doctor picked from the catalogue
                      (drug_code is the Drug.id) — free-text items have no
                      catalogue entry so there's nothing to look up. */}
                  {it.drug_code && (
                    <>
                      <DrugAvailabilityHint drugId={it.drug_code} pharmacyId={pharmacyId} />
                      <SafetyHint
                        drugId={it.drug_code}
                        patientId={encounter?.patient}
                        // Pass IDs of every OTHER picked drug in this same
                        // form so the backend treats them as "active" — that's
                        // how within-form interactions get caught before submit.
                        otherDrugIds={
                          items
                            .filter((x, idx) => idx !== i && x.drug_code)
                            .map((x) => x.drug_code)
                        }
                      />
                    </>
                  )}
                  </>
                ) : (
                  <CatalogueAutocomplete
                    queryKey={`drugs-${pharmacyId || 'none'}`}
                    searchFn={searchDrugs}
                    placeholder="Search drug name or code…"
                    rowKey={(d) => d.code}
                    quickPicks={COMMON_DRUGS}
                    allowFreeText
                    freeTextLabel="Use as typed (drug not in this pharmacy's catalogue)"
                    renderRow={(d) => (
                      <>
                        <span className="font-mono text-xs text-wantok-700">{d.code}</span>
                        <span className="ml-2 font-medium">{d.name}</span>
                        {d.strength && <span className="text-slate-500 ml-2 text-xs">{d.strength}</span>}
                        {d.dosage_form && <span className="text-slate-400 ml-2 text-xs italic">{d.dosage_form}</span>}
                        {d.is_otc && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">OTC</span>}
                        {d.is_controlled && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-800 border-amber-200">controlled</span>}
                      </>
                    )}
                    onPick={(d) => updateItem(i, {
                      drug_code: d.code || '',
                      drug_name: d.name || '',
                      strength: d.strength || '',
                    })}
                  />
                )}

                {/* Route of administration */}
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">Route</span>
                  <select
                    value={it.route || 'PO'}
                    onChange={(e) => updateItem(i, { route: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
                  >
                    {ROUTES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>

                {/* Dose / frequency / duration / qty */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <input
                    placeholder="Dose (e.g. 1 g)"
                    value={it.dose}
                    onChange={(e) => updateItem(i, { dose: e.target.value })}
                    className="border rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    placeholder="Frequency (e.g. qid, q8h)"
                    value={it.frequency}
                    onChange={(e) => updateItem(i, { frequency: e.target.value })}
                    className="border rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    placeholder="Duration (e.g. 5 days)"
                    value={it.duration}
                    onChange={(e) => updateItem(i, { duration: e.target.value })}
                    className="border rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    placeholder="Qty to dispense"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, { quantity: e.target.value })}
                    className="border rounded px-2 py-1.5 text-sm"
                  />
                </div>

                {/* Auto-calculated quantity hint */}
                <QuantityHint item={it} onApply={(n) => updateItem(i, { quantity: String(n) })} />

                <input placeholder="Instructions to patient (optional)" value={it.instructions} onChange={(e) => updateItem(i, { instructions: e.target.value })} className="border rounded px-2 py-1.5 text-sm w-full" />
              </div>
            ))}

            <button type="button" onClick={() => setItems((it) => [...it, newItem()])} className="text-xs text-wantok-700 underline">+ Add another drug</button>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Notes to pharmacy</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
          </label>

          {(formError || mut.error) && <ErrorBanner error={formError || mut.error} />}

          <div className="flex justify-end">
            <button disabled={mut.isPending} className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60">
              {mut.isPending ? 'Saving…' : 'Add prescription'}
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}

/**
 * Tiny helper that renders the calculator result underneath the qty input.
 * It parses the doctor's inputs (dose, strength, frequency, duration) and
 * shows a friendly hint like:
 *
 *   Suggested: 2 tabs × 4/day × 5 days = 40 tabs  [Apply]
 *
 * Clicking Apply copies the total into the Qty field. If any input is
 * unparseable we just hide the hint — better silent than a wrong answer
 * for a clinical calculation.
 */
function QuantityHint({ item, onApply }) {
  const result = calculateQuantity({
    dose: item.dose,
    strength: item.strength,
    frequency: item.frequency,
    duration: item.duration,
  })
  if (!result) return null
  const total = totalForDispensing(result)
  const current = String(item.quantity || '').trim()
  const alreadyApplied = current !== '' && Number(current) === Number(total)

  return (
    <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2 bg-wantok-50/60 border border-wantok-100 rounded px-2 py-1">
      <span className="text-wantok-800">
        <span className="uppercase tracking-wide text-[10px] text-wantok-700 mr-1">Suggested</span>
        {formatQuantityHint(result)}
      </span>
      {alreadyApplied ? (
        <span className="text-emerald-700 font-medium">· Applied ✓</span>
      ) : (
        <button
          type="button"
          onClick={() => onApply(total)}
          className="ml-auto text-[11px] text-wantok-700 underline decoration-dotted underline-offset-2"
        >
          Apply {total}
        </button>
      )}
    </div>
  )
}

/**
 * Inline availability hint for the picked drug. Tells the doctor whether
 * the chosen target pharmacy actually stocks it, and at what price. If
 * not, suggests pharmacies that do.
 *
 * Three states:
 *   - Loading                 → muted spinner row.
 *   - Stocked here            → green chip with brand + price + stock.
 *   - Not stocked here        → amber row listing alternative pharmacies.
 */
/**
 * Clinical safety hint — calls /api/pharmacies/safety-check/ for the
 * picked drug + this patient, and renders any allergy or DDI warnings.
 *
 * - Allergies are shown as a rose-toned alert (clinically the most
 *   serious — wrong-class-of-drug-to-an-allergic-patient).
 * - Major / contraindicated DDIs are amber.
 * - Moderate / minor DDIs are slate.
 */
function SafetyHint({ drugId, patientId, otherDrugIds = [] }) {
  // Stable comma-joined string so React Query keys/dedupes correctly.
  const additional = (otherDrugIds || []).filter(Boolean).map(String).sort().join(',')
  const safetyQ = useQuery({
    queryKey: ['safety-check', drugId, patientId, additional],
    queryFn: async () => {
      const params = { drug_id: drugId, patient_id: patientId }
      if (additional) params.additional_drug_ids = additional
      const { data } = await api.get('/api/pharmacies/safety-check/', { params })
      return data
    },
    enabled: Boolean(drugId && patientId),
    staleTime: 15_000,
  })
  if (safetyQ.isLoading) {
    return <div className="text-[11px] text-slate-400 italic">Checking allergies & interactions…</div>
  }
  if (safetyQ.error || !safetyQ.data) return null
  const { allergies = [], interactions = [] } = safetyQ.data
  if (allergies.length === 0 && interactions.length === 0) {
    return (
      <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
        ✓ No allergy or interaction concerns identified.
      </div>
    )
  }
  return (
    <div className="space-y-1.5">
      {allergies.map((a, i) => (
        <div key={`al-${i}`} className="text-[12px] bg-rose-50 border border-rose-200 text-rose-900 rounded px-2.5 py-1.5">
          <div className="font-semibold flex items-center gap-1.5">
            <span>⚠ Allergy alert</span>
            <span className="text-[10px] uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200 rounded-full px-1.5 py-0.5 font-bold">
              {a.severity}
            </span>
          </div>
          <div className="mt-0.5">{a.message}</div>
          {a.source && <div className="text-[10px] text-rose-700/80 mt-0.5">Source: {a.source}</div>}
        </div>
      ))}
      {interactions.map((d, i) => {
        const tone = d.severity === 'contraindicated' ? 'rose'
                  : d.severity === 'major'           ? 'amber'
                  : d.severity === 'moderate'        ? 'sky'
                  : 'slate'
        const tones = {
          rose:  'bg-rose-50 border-rose-200 text-rose-900',
          amber: 'bg-amber-50 border-amber-200 text-amber-900',
          sky:   'bg-sky-50 border-sky-200 text-sky-900',
          slate: 'bg-slate-50 border-slate-200 text-slate-700',
        }
        return (
          <div key={`dd-${i}`} className={`text-[12px] rounded px-2.5 py-1.5 border ${tones[tone]}`}>
            <div className="font-semibold flex items-center gap-1.5">
              <span>⚠ Drug interaction · {d.rule_a} × {d.rule_b}</span>
              <span className="text-[10px] uppercase tracking-wider bg-white/60 border rounded-full px-1.5 py-0.5 font-bold">
                {d.severity}
              </span>
            </div>
            <div className="mt-0.5">
              {d.effect}.
              <span className="text-slate-700"> Patient is currently on <b>{d.with_drug}</b>.</span>
            </div>
            {d.management && (
              <div className="text-[11px] mt-0.5 italic">
                {d.management}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function DrugAvailabilityHint({ drugId, pharmacyId }) {
  const stockQ = useQuery({
    queryKey: ['drug-stock', drugId],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.drugSearch}${drugId}/stock/`)
      return data
    },
    enabled: Boolean(drugId),
    staleTime: 30_000,
  })
  if (stockQ.isLoading) {
    return <div className="text-[11px] text-slate-400 italic">Checking pharmacy availability…</div>
  }
  if (stockQ.error || !Array.isArray(stockQ.data)) return null
  const rows = stockQ.data
  if (rows.length === 0) {
    return (
      <div className="text-[11px] bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1">
        No pharmacy on the platform currently stocks this drug. The order
        will still send as <b>free-text</b>; the receiving pharmacy can
        request the drug or refuse.
      </div>
    )
  }
  // Coerce both sides to a number — pharmacyId comes from <select> as a
  // string, while r.pharmacy is the FK integer. Strict-equality would
  // miss the match and the user would see "stocked elsewhere by THIS
  // pharmacy" — which is exactly what was happening.
  const target = pharmacyId != null ? Number(pharmacyId) : null
  const here = target != null ? rows.find((r) => Number(r.pharmacy) === target) : null
  const others = rows.filter((r) => Number(r.pharmacy) !== target)

  if (here) {
    return (
      <div className="text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-1 flex flex-wrap items-center gap-2">
        <span className="font-medium">✓ Stocked at this pharmacy</span>
        {here.brand_name && <span>· brand <b>{here.brand_name}</b></span>}
        {here.price != null && <span>· PGK <b>{here.price}</b></span>}
        {here.stock_quantity > 0 && <span>· {here.stock_quantity} in stock</span>}
      </div>
    )
  }

  // Not stocked at the chosen pharmacy.
  return (
    <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
      <div className="font-medium">
        ⚠ {pharmacyId ? 'Not stocked at the pharmacy you picked.' : 'Pick a pharmacy to see availability.'}
        {others.length > 0 && ` Stocked elsewhere by ${others.length} pharmac${others.length === 1 ? 'y' : 'ies'}:`}
      </div>
      {others.length > 0 && (
        <ul className="mt-1 ml-2">
          {others.slice(0, 5).map((r) => (
            <li key={r.id}>
              · {r.pharmacy_name}
              {r.price != null && <> — PGK {r.price}</>}
              {r.brand_name && <> ({r.brand_name})</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// RxItemRow — per-item dispensing display.
//
// Each prescribed drug shows its dispensing status badge (Pending /
// Dispensed / Partial / Substituted / Not Available). Substitutions
// surface the new drug name; partials surface the dispensed quantity;
// not-available items show a warning + "Route to another pharmacy"
// button so the doctor can redirect the script in one click.
// ---------------------------------------------------------------------------

const ITEM_STATUS_TONE = {
  pending:             { bg: 'bg-slate-100',   text: 'text-slate-700',   label: 'Pending' },
  dispensed:           { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Dispensed' },
  partially_dispensed: { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Partial' },
  substituted:         { bg: 'bg-violet-100',  text: 'text-violet-800',  label: 'Substituted' },
  not_available:       { bg: 'bg-rose-100',    text: 'text-rose-800',    label: 'Not available' },
}

function RxItemRow({ item: it, prescriptionId, onRerouted }) {
  const tone = ITEM_STATUS_TONE[(it.dispensing_status || 'pending').toLowerCase()] || ITEM_STATUS_TONE.pending
  const isUnavailable = it.dispensing_status === 'not_available'
  const [reroutingOpen, setReroutingOpen] = useState(false)

  return (
    <li className="py-2 text-sm">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-slate-800 inline-flex items-center gap-2 flex-wrap">
            {it.drug_name}
            {it.drug_code && <span className="font-mono text-[11px] text-slate-400">{it.drug_code}</span>}
            <span className={`text-[10px] font-bold uppercase tracking-wide ${tone.bg} ${tone.text} rounded-full px-2 py-0.5`}>
              {tone.label}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {it.dose && <span>Dose: <b>{it.dose}</b></span>}
            {it.frequency && <span>Frequency: <b>{it.frequency}</b></span>}
            {it.duration && <span>Duration: <b>{it.duration}</b></span>}
            {it.quantity != null && <span>Qty: <b>{it.quantity}</b></span>}
          </div>
          {it.instructions && (
            <div className="text-[11px] text-slate-500 mt-0.5 italic">{it.instructions}</div>
          )}
        </div>
      </div>

      {/* Substitution detail */}
      {it.dispensing_status === 'substituted' && it.substituted_drug_name && (
        <div className="mt-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1">
          <strong className="font-bold">Substituted with:</strong> {it.substituted_drug_name}
          {it.pharmacist_comment && <> — {it.pharmacist_comment}</>}
        </div>
      )}

      {/* Partial-fill detail */}
      {it.dispensing_status === 'partially_dispensed' && it.dispensed_quantity && (
        <div className="mt-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
          <strong className="font-bold">Dispensed:</strong> {it.dispensed_quantity}
          {it.pharmacist_comment && <> — {it.pharmacist_comment}</>}
        </div>
      )}

      {/* Unavailable warning + reroute action */}
      {isUnavailable && (
        <div className="mt-1.5 text-xs bg-rose-50 border-2 border-rose-200 text-rose-900 rounded p-2">
          <div className="font-bold mb-1">⚠ Not available — consider sending to another pharmacy.</div>
          {it.unavailable_reason && (
            <div className="mb-2"><strong>Reason:</strong> {it.unavailable_reason}</div>
          )}
          <button
            onClick={() => setReroutingOpen(true)}
            className="text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded px-3 py-1.5"
          >
            Route prescription to another pharmacy
          </button>
        </div>
      )}

      {/* Per-item pharmacist note (when not folded into substitute/partial blocks) */}
      {it.pharmacist_comment && it.dispensing_status === 'dispensed' && (
        <div className="mt-1 text-[11px] text-slate-500 italic">
          Pharmacist: {it.pharmacist_comment}
        </div>
      )}

      {reroutingOpen && (
        <ReroutePrescriptionModal
          prescriptionId={prescriptionId}
          onClose={() => setReroutingOpen(false)}
          onSuccess={(newRx) => {
            setReroutingOpen(false)
            onRerouted?.(newRx)
          }}
        />
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// ReroutePrescriptionModal — pick another pharmacy → POST reroute.
// ---------------------------------------------------------------------------

function ReroutePrescriptionModal({ prescriptionId, onClose, onSuccess }) {
  const [pharmacyId, setPharmacyId] = useState('')
  const [reason, setReason] = useState('Original pharmacy could not supply')
  const pharmacyQ = useQuery({
    queryKey: ['org-list', 'pharmacy'],
    queryFn: async () => {
      const { data } = await api.get(endpoints.pharmacyList)
      return data?.results || data || []
    },
    staleTime: 60_000,
  })
  const mut = useMutation({
    mutationFn: () => reroutePrescription(prescriptionId, { pharmacy_id: Number(pharmacyId), reason }),
    onSuccess,
  })

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-bold text-slate-900 text-lg">Route to another pharmacy</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          A new prescription with the same items will be created for the
          chosen pharmacy. The original prescription is preserved unchanged.
        </p>

        <div className="mb-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            New pharmacy
          </label>
          <select
            value={pharmacyId}
            onChange={(e) => setPharmacyId(e.target.value)}
            className="w-full border-2 border-slate-200 focus:border-wantok-600 rounded-lg px-3 py-2 text-base"
          >
            <option value="">Select a pharmacy…</option>
            {(pharmacyQ.data || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            Reason (optional)
          </label>
          <input
            type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full border-2 border-slate-200 focus:border-wantok-600 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {mut.isError && (
          <div className="mb-3 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1.5">
            {mut.error?.response?.data?.detail || mut.error?.message || 'Failed to reroute.'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t-2 border-slate-100">
          <button onClick={onClose} className="text-sm font-medium text-slate-600 hover:bg-slate-100 rounded px-4 py-2">Cancel</button>
          <button
            onClick={() => mut.mutate()}
            disabled={!pharmacyId || mut.isPending}
            className="text-sm font-semibold bg-wantok-700 hover:bg-wantok-800 disabled:bg-slate-300 text-white rounded-lg px-5 py-2"
          >
            {mut.isPending ? 'Rerouting…' : 'Reroute prescription'}
          </button>
        </div>
      </div>
    </div>
  )
}
